// src-lit/components/product/kd-product-share.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { onEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';

interface ShareVariantEntry {
  /** LINE シェア URL（variant の url / title / 割引率を含む） */
  lineUrl: string;
  /** X (Twitter) シェア URL */
  xUrl: string;
  /** Facebook シェア URL */
  facebookUrl: string;
}

/**
 * @element kd-product-share
 * @description variant 切替に応じて LINE / X / Facebook の各シェア URL を更新する。
 *   variant ごとの URL は Liquid 側で precompute されている（getProductVariantTitle / calcDiscountRate
 *   の TS 二重化を避けるため）。Lit は href を差し替えるのみ。
 */
@customElement('kd-product-share')
export class KdProductShare extends LitElement {
  createRenderRoot() {
    return this;
  }

  private unsubscribe: (() => void) | null = null;

  private variantMap: Record<string, ShareVariantEntry | undefined> = {};

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.loadVariantMap();
    this.unsubscribe = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => this.handleVariantSelected(e.detail),
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private loadVariantMap(): void {
    const script = this.querySelector<HTMLScriptElement>(
      SELECTORS.PRODUCT_SHARE.VARIANT_MAP,
    );
    if (!script?.textContent) return;
    try {
      this.variantMap = JSON.parse(script.textContent);
    } catch {
      // JSON parse 失敗時は空マップのまま
    }
  }

  private handleVariantSelected(detail: VariantSelectedDetail): void {
    const entry = this.variantMap[detail.variantId];
    if (!entry) return;
    this.updateHref(SELECTORS.PRODUCT_SHARE.LINK_LINE, entry.lineUrl);
    this.updateHref(SELECTORS.PRODUCT_SHARE.LINK_X, entry.xUrl);
    this.updateHref(SELECTORS.PRODUCT_SHARE.LINK_FACEBOOK, entry.facebookUrl);
  }

  private updateHref(selector: string, url: string): void {
    const link = this.querySelector<HTMLAnchorElement>(selector);
    if (link) link.setAttribute('href', url);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-product-share': KdProductShare;
  }
}
